<template>
  <el-container class="background-white">
    <el-header class="header-container">
      <h1 class="header-title">Missing Orders <small>(Orders not in Kitsilano)</small></h1>
    </el-header>

    <el-main class="main-content">
      <!-- Top Controls Row -->
      <el-row :gutter="20" class="controls-row" align="middle">
        <el-col :xs="24" :sm="8">
          <el-switch
              v-model="autoRefresh"
              :disabled="loading"
              active-text="Auto-Refresh On"
              inactive-text="Auto-Refresh Off"
              class="custom-switch">
          </el-switch>
        </el-col>

        <!-- Combined Filters in One Row -->
        <el-col :xs="24" :sm="8" class="filter-container">
          <el-select
              v-model="status"
              size="mini"
              placeholder="Select Status"
              @change="fetch"
              class="dropdown custom-select"
          >
            <el-option
                v-for="item in statusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
            </el-option>
          </el-select>
          <el-select
              v-model="additionalFilter"
              size="mini"
              placeholder="Filter Orders"
              @change="fetch"
              class="dropdown custom-select additional-filter"
          >
            <el-option
                v-for="item in additionalFilterOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
            </el-option>
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="8" class="text-right">
          <el-button
              type="primary"
              size="mini"
              @click="fetch"
              :disabled="loading || autoRefresh"
              style="margin-right: 10px">
            <i class="el-icon-refresh"></i>
            Refresh
          </el-button>
          <el-button
              type="warning"
              size="mini"
              @click="reprocessAll"
              :disabled="loading || !orders.length">
            <i class="el-icon-refresh-right"></i>
            Reprocess All
          </el-button>
        </el-col>
      </el-row>

      <!-- Orders Table -->
      <el-row>
        <el-col :span="24">
          <div class="table-container">
            <el-table
                v-loading="loading"
                :data="orders"
                class="order-table"
                stripe
                border
                max-height="600">
              <!-- Table Columns -->
              <el-table-column
                  prop="sales_channel"
                  label="Sales Channel"
                  min-width="150">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.sales_channel }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="region"
                  label="Store"
                  min-width="120">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.region }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="order_reference"
                  label="Order ID"
                  min-width="160">
                <template #default="{ row }">
                  <div class="order-id-cell">
                    <a
                        v-if="row.order_link"
                        :href="row.order_link"
                        class="order-id"
                        target="_blank"
                        rel="noopener noreferrer">
                      {{ row.order_reference }}
                    </a>
                    <span v-else class="order-id">{{ row.order_reference }}</span>
                    <span v-if="row.bwp_order_id" class="bwp-order-id">BWP: {{ row.bwp_order_id }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                  prop="fulfillment_channel"
                  label="Fulfillment Channel"
                  min-width="180">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.fulfillment_channel }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Created at"
                  min-width="180">
                <template #default="{ row }">
                  <span class="date-cell">{{ displayDate(row.created_at) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Send to D365"
                  min-width="180">
                <template #default="{ row }">
                  <span class="date-cell">{{ displayDate(row.send_to_erp_at) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="status"
                  label="Status"
                  min-width="180">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.status_title }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Shipment Status"
                  min-width="180">
                <template #default="{ row }">
                  <span class="table-cell" style="white-space: break-spaces">{{ shipmentStatus(row.fulfillment_status || []) }}</span>
                </template>
              </el-table-column>
              <!-- Reprocess Button -->
              <el-table-column label="Actions" align="right" min-width="140">
                <template #default="{ row }">
                  <el-button size="mini" type="warning" @click.native.prevent="reprocess(row.id, row.status)"
                             class="action-button">
                    <i class="el-icon-refresh-right"></i>
                    Reprocess
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>
    </el-main>
  </el-container>
</template>

<style scoped>
.controls-row {
  margin-bottom: 25px;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.filter-container {
  display: flex;
  gap: 10px; /* Adds space between filters */
  align-items: center;
}

.dropdown.custom-select {
  width: 100%;
}

.additional-filter {
  width: 100%;
}

.text-right {
  text-align: right;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 25px;
  border-bottom: 2px solid #e8e8e8;
  background: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.main-content {
  padding: 25px;
  background: #fff;
}

.controls-row {
  margin-bottom: 25px;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.custom-switch {
  font-weight: 500;
}

.dropdown.custom-select {
  margin-right: 10px;
  width: 100%;
  border-radius: 6px;
}

.refresh-button {
  transition: all 0.3s ease;
  font-weight: 500;
}

.refresh-button:hover {
  transform: scale(1.02);
}

.table-container {
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.order-table {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.order-id-cell {
  display: flex;
  flex-direction: column;
}

.table-cell {
  color: #2c3e50;
  font-weight: 500;
}

.order-id {
  color: #409EFF;
  font-weight: 600;
}

.bwp-order-id {
  color: #909399;
  font-size: 0.85em;
}

.date-cell {
  color: #606266;
  font-size: 0.9em;
}

.action-button {
  transition: all 0.2s ease;
  border-radius: 6px;
}

.action-button:hover {
  transform: scale(1.05);
}

@media screen and (max-width: 768px) {
  .controls-row .el-col {
    margin-bottom: 15px;
    text-align: left !important;
  }

  .header-title {
    font-size: 20px;
  }

  .main-content {
    padding: 15px;
  }
}
</style>

<script>
import axios, {RepositoryError} from '../../../repositories';

export default {
  name: 'MissingOrdersPage',
  created() {
    this.fetch();
  },
  data() {
    return {
      autoRefresh: false,
      autoRefreshInterval: 10000,
      refreshInterval: null,
      loading: true,
      orders: [],
      status: 'withoutFBA', // Main status filter
      additionalFilter: 'order_not_in_kitsilano', // Secondary filter for additional conditions
      statusOptions: [
        {value: 'all', label: 'All'},
        {value: 'withoutFBA', label: 'Without FBA'},
      ],
      additionalFilterOptions: [
        {value: 'all', label: 'All Orders'},
        {value: 'order_not_in_kitsilano', label: 'Order Not in Kitsilano'},
        {value: 'order_not_in_erp', label: 'Order Not in D365'},
      ],
    };
  },
  methods: {
    async fetch() {
      this.loading = true;

      // Determine parameters for the request
      let withFBA = false;
      let filterStatus = null;

      // Determine the `withFBA` parameter based on the main status filter
      if (this.status === 'all') {
        withFBA = true;
      } else if (this.status === 'withoutFBA') {
        withFBA = false;
      }

      // Determine the `filterStatus` parameter based on the additional filter
      if (this.additionalFilter !== 'all') {
        filterStatus = this.additionalFilter; // E.g., 'order_not_in_kitsilano' or 'order_not_in_erp'
      }

      try {
        // Fetch data from the API with both filters applied
        const response = await axios.get('/kitsilano/orders/order-log', {
          params: {withFBA, status: filterStatus},
        });

        if (response.data?.success === 'true' || response.data?.success === true) {
          // Map orders to include `status_title` logic
          this.orders = (response.data?.pending_orders || []).map((order) => {
            if (order.status === 'order_not_in_erp') {
              order.status_title = 'Order Not In D365';
            } else if (order.status === 'order_not_in_kitsilano') {
              order.status_title = 'Order Not In Kitsilano';
            } else {
              order.status_title = order.status;
            }
            return order;
          });
        } else {
          this.$message.error(response.data?.message || 'Error loading orders');
        }
      } catch (err) {
        this.$message.error(err.message || 'Repository error occurred');
      } finally {
        this.loading = false;
      }
    },
    async reprocess(orderId, status) {
      this.loading = true;

      try {
        const response = await axios.put(`/kitsilano/orders/order-log/reprocess`, { orderId, status });

        if (response.data?.success === false) {
          this.$message.error(response.data?.message || `Failed to reprocess order ID ${orderId}.`);
        } else {
          this.$message.success(`Order ID ${orderId} reprocessed successfully.`);
          await this.fetch();
        }
      } catch (err) {
        let errorMessage = `An unexpected error occurred while reprocessing Order ID ${orderId}.`;

        if (err.response && err.response.data?.message) {
          // Use ONLY the backend error message and ignore Axios error messages
          errorMessage = err.response.data.message;
        }

        this.$message.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    async reprocessAll() {
      if (!this.orders.length) {
        this.$message.warning("No orders to reprocess.");
        return;
      }

      const ordersData = this.orders.map(order => ({
        orderId: order.id,
        status: order.status,
      }));

      this.loading = true;

      try {
        const response = await axios.put(
            `/kitsilano/orders/order-log/reprocess-all`,
            { orders: ordersData }
        );

        if (response.data?.success === false) {
          // Check if there are errors in the response
          const errors = response.data?.errors;
          if (Array.isArray(errors) && errors.length > 0) {
            // Convert errors to an HTML list
            const errorList = errors.map((error) => `<li>${error}</li>`).join("");
            this.$message({
              dangerouslyUseHTMLString: true,
              message: `<strong>Some orders failed to reprocess:</strong><ul>${errorList}</ul>`,
              type: "error",
              duration: 8000,
            });
          } else {
            this.$message.error(response.data?.message || "Unknown error occurred.");
          }
        } else {
          this.$message.success(response.data?.message || "All orders reprocessed successfully.");
          await this.fetch(); // Refresh the data after successful reprocessing
        }
      } catch (err) {
        this.$message.error(err.message || "Unexpected error occurred while reprocessing orders.");
      } finally {
        this.loading = false;
      }
    },
    displayDate(date) {
      if (!date) {
        return '-';
      }

      try {
        const formattedDate = new Date(date);
        return formattedDate.toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Use 24-hour format
          timeZoneName: 'short', // Show time zone
        });
      } catch (error) {
        console.error('Invalid date format:', date);
        return '-';
      }
    },
    shipmentStatus(data) {
      let shipmentStatus = '';
      data.forEach(item => {
        shipmentStatus += `${item['fulfillment_center']}: ${item['control_number'] ? 'Synced to D365' : 'Not Synced'}\n`
      })
      return shipmentStatus;
    }
  },
  watch: {
    autoRefresh: function (val) {
      if (val) {
        return this.refreshInterval = setInterval(
            () => this.fetch(),
            this.autoRefreshInterval,
        );
      }

      clearTimeout(this.refreshInterval);
    },
  },
};
</script>
